/**
 * Global environment selection (W1b).
 *
 * One source of truth for "which environment am I looking at?" — replacing the
 * three ad-hoc per-page models the audit found (Monitor read ?env=, Dashboard
 * hardcoded the list, Costs had none). Precedence for the initial value (RT-16):
 *   URL ?env=  >  persisted localStorage  >  default (dev).
 *
 * On every switch we drop all cached React-Query data (RT-3) so one environment's
 * numbers can never bleed through to another, and we keep ?env= in the URL in sync
 * so a link carries its environment.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export type Environment = 'dev' | 'test' | 'uat' | 'prod';
export const ENVIRONMENTS: Environment[] = ['dev', 'test', 'uat', 'prod'];

// Environments that actually have a provisioned spoke today. UAT/PROD are Phase-2
// and render as disabled "not provisioned" in the selector. W2 replaces this
// constant with a live provisioned-matrix endpoint (see plan §5.2 / RT-16).
export const PROVISIONED_ENVIRONMENTS: Environment[] = ['dev', 'test'];

const STORAGE_KEY = 'dashboard.environment';
const DEFAULT_ENV: Environment = 'dev';

function isEnv(v: unknown): v is Environment {
  return typeof v === 'string' && (ENVIRONMENTS as string[]).includes(v);
}

export function readInitialEnvironment(): Environment {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('env');
    if (isEnv(fromUrl)) return fromUrl;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isEnv(stored)) return stored;
  } catch {
    /* storage / URL unavailable — fall through to default */
  }
  return DEFAULT_ENV;
}

interface EnvironmentContextValue {
  environment: Environment;
  setEnvironment: (env: Environment) => void;
  environments: Environment[];
  provisioned: Environment[];
}

const EnvironmentContext = createContext<EnvironmentContextValue | undefined>(undefined);

export const EnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [environment, setEnvState] = useState<Environment>(readInitialEnvironment);

  const setEnvironment = useCallback((env: Environment) => {
    setEnvState((prev) => {
      if (prev === env) return prev;
      try { window.localStorage.setItem(STORAGE_KEY, env); } catch { /* ignore */ }
      // RT-3: purge cached query data so the new env refetches cleanly and no
      // previous-env values are shown under the new label.
      queryClient.removeQueries();
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('env', env);
        window.history.replaceState({}, '', url.toString());
      } catch { /* ignore */ }
      return env;
    });
  }, [queryClient]);

  // Seed persistence on first mount if nothing is stored yet.
  useEffect(() => {
    try {
      if (!window.localStorage.getItem(STORAGE_KEY)) {
        window.localStorage.setItem(STORAGE_KEY, environment);
      }
    } catch { /* ignore */ }
  }, [environment]);

  return (
    <EnvironmentContext.Provider
      value={{ environment, setEnvironment, environments: ENVIRONMENTS, provisioned: PROVISIONED_ENVIRONMENTS }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
};

export function useEnvironment(): EnvironmentContextValue {
  const ctx = useContext(EnvironmentContext);
  if (!ctx) throw new Error('useEnvironment must be used within an EnvironmentProvider');
  return ctx;
}

/**
 * Env-keyed query-key factory (RT-3). Prefix every environment-scoped React-Query
 * key with the active environment so the cache is partitioned per env and a switch
 * can never serve stale cross-env data. Pages adopt this as they migrate.
 *   useQuery({ queryKey: envKey(environment, 'costs', period), ... })
 */
export function envKey(environment: Environment, ...parts: unknown[]): unknown[] {
  return ['env', environment, ...parts];
}
